import LottieImport from 'lottie-react';
import successAnimation from '../../assets/success-animation.json';
import { Button, Modal, RingProgress, Stack, Text } from '@mantine/core';
import { DownloadIcon, XIcon } from 'lucide-react';

// `lottie-react` is CommonJS; under @vitejs/plugin-react v6 (Oxc) the
// default-import shape can resolve to `{ default: Component }` instead of the
// component itself, which crashes React with error #130. Coerce defensively so
// the import works regardless of interop mode.
const Lottie = ((
  LottieImport as unknown as {
    default?: typeof LottieImport;
  }
).default ?? LottieImport) as typeof LottieImport;

export const SaveDialog = ({
  signatureStatus,
  pctSaved,
  onCancelClick,
  onSaveACopyClick,
}: {
  signatureStatus: 'unsigned' | 'signed' | 'saving' | 'saved';
  pctSaved: number;
  onCancelClick: () => void;
  onSaveACopyClick: () => void;
}) => {
  const isSaving = signatureStatus === 'saving';

  return (
    <Modal
      opened={['saving', 'saved'].includes(signatureStatus)}
      onClose={() => {}}
      withCloseButton={false}
      centered
      size='auto'
      radius='xl'
      padding='xl'
    >
      <Stack gap='xl' justify='center' align='center' w='360px'>
        {isSaving ? (
          <div
            style={{
              height: '160px',
              width: '160px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <RingProgress
              label={
                <Text ta='center' size='sm' c='gray.9'>
                  {pctSaved > 0 ? `${Math.round(pctSaved)}%` : '0%'}
                </Text>
              }
              sections={[{ value: pctSaved, color: '#79f655' }]}
              size={68}
              thickness={4}
            />
          </div>
        ) : (
          <Lottie
            animationData={successAnimation}
            style={{ height: '160px' }}
            loop={false}
          />
        )}
        <Stack gap='md' align='center' mb='xl' h='98px'>
          <Text size='32px' fw={600}>
            {isSaving ? 'Saving' : 'Success!'}
          </Text>

          <Text size='' c='gray.7' ta='center'>
            {isSaving
              ? `This should only take a moment.`
              : `We've received your signed engagement letter and will reach out soon with next steps.`}
          </Text>
        </Stack>
        {isSaving ? (
          <Button
            variant='outline'
            c='gray.9'
            bd='1px solid var(--mantine-color-gray-4)'
            leftSection={<XIcon size={16} />}
            onClick={onCancelClick}
          >
            Cancel
          </Button>
        ) : (
          <Button
            variant='outline'
            c='gray.9'
            bd='1px solid var(--mantine-color-green-4)'
            leftSection={<DownloadIcon size={16} />}
            onClick={onSaveACopyClick}
          >
            Save a PDF copy
          </Button>
        )}
      </Stack>
    </Modal>
  );
};
